import {TherapySequenceActions} from '@/modules/patients-therapies/components/patient-therapy-sequence-actions'
import {openPatientTherapySequenceDrawer} from '@/modules/patients-therapies/components/patient-therapy-sequence-drawer/util'
import {TherapySequenceJourneyTimeline} from '@/modules/patients-therapies/components/patient-therapy-sequence-journey-timeline'
import {formatTreatmentLabels, getSequencePatientSummary} from '@/modules/patients-therapies/functions'
import {getPatientFullName} from '@/modules/patients/functions'
import {useGetSettings} from '@/modules/settings/api'
import {ResourceStatus} from '@/shared/components/ui/resource-status'
import {Box, BoxProps, Button, chakra, Checkbox, Status, Text} from '@chakra-ui/react'
import {PatientTherapySequence} from '@cypro/engine/schema'
import {SortableTableHeader} from '@cypro/ui/components/ui/sortable-table-header'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {AccessorColDef, DisplayColDef} from '@cypro/ui/types'
import {getShortDateFormatter} from '@cypro/ui/utils'
import {createColumnHelper} from '@tanstack/react-table'
import {useTranslate} from '@tolgee/react'
import {LuQuote} from 'react-icons/lu'

const sequenceColumnsHelper = createColumnHelper<PatientTherapySequence>()

type AccessorColumnDef = AccessorColDef<PatientTherapySequence>
type DisplayColumnDef = DisplayColDef<PatientTherapySequence>

/**
 * The protocol, treatment, administration, internalId and journey columns should be hidden if the sequence is a note, so we pass props using the cell's meta properties.
 *
 * We also display the note using the protocol column, because it should span from the start of the protocol column to the end of the journey column.
 */
function getHideableCellProps(sequence: PatientTherapySequence) {
  const props: BoxProps = {}
  if (sequence.isNote) {
    props.maxWidth = '0'
    props.minWidth = '0'
    props.padding = '0'
  }
  return props
}

function PatientCell({sequence, showHospitalId = false}: {sequence: PatientTherapySequence; showHospitalId?: boolean}) {
  const {t} = useTranslate('common')
  const {settings} = useGetSettings()
  const patient = sequence.patient

  if (!patient) {
    return (
      <Text fontSize="sm" color="fg.muted">
        {t('not_available')}
      </Text>
    )
  }

  const hospitalPatientId =
    showHospitalId && (settings?.showHospitalPatientId ?? true) ? patient.hospitalPatientId : null

  return (
    <Box>
      <Box display="flex" alignItems="center" gap="1" minWidth="0" width="100%">
        <Text fontSize="sm" truncate minWidth="0" flexShrink={1}>
          {getPatientFullName(patient)}
        </Text>
        {hospitalPatientId && (
          <>
            <Text fontSize="sm" color="fg.muted" flexShrink={0}>
              •
            </Text>
            <Tooltip content={t('hospital_id')}>
              <Box display="flex" alignItems="center" gap="1">
                <Text fontSize="xs" color="fg.muted" flexShrink={0}>
                  ID
                </Text>
                <Text fontSize="xs" whiteSpace="nowrap" flexShrink={0}>
                  {hospitalPatientId}
                </Text>
              </Box>
            </Tooltip>
          </>
        )}
      </Box>
      <Text fontSize="xs" color="fg.muted">
        {getSequencePatientSummary(sequence) || '-'}
      </Text>
    </Box>
  )
}

export const PatientTherapySequencesColumnsDef = {
  select: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'select',
      minSize: 42, // px
      header: ({table}) => {
        const allSelected = table.getIsAllRowsSelected()
        const isIndeterminate = table.getIsSomeRowsSelected() && !allSelected

        return (
          <Checkbox.Root
            size="md"
            aria-label="Select row"
            checked={isIndeterminate ? 'indeterminate' : allSelected}
            onCheckedChange={changes => table.toggleAllRowsSelected(Boolean(changes.checked))}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
          </Checkbox.Root>
        )
      },
      cell: ({row}) => {
        return (
          // a plain click ticks only this row; a shift-click is left to the row, which selects the range from
          // the anchor, and the box's own toggle is cancelled so the row is not toggled twice
          <chakra.div
            onClickCapture={e => {
              if (e.shiftKey) e.preventDefault()
            }}
            onClick={e => {
              if (!e.shiftKey) e.stopPropagation()
            }}
          >
            <Checkbox.Root
              size="md"
              aria-label="Select row"
              checked={row.getIsSelected()}
              onCheckedChange={changes => row.toggleSelected(Boolean(changes.checked))}
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control />
            </Checkbox.Root>
          </chakra.div>
        )
      },
      enableSorting: false,
      ...def,
    }),

  observationsBubble: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'observations',
      header: () => {
        return null
      },
      cell: ({row}) => {
        const {t} = useTranslate('common')
        if (!row.original.observations?.length) return null

        const label = t('x_observations', {value: row.original.observations.length})

        return (
          <Tooltip content={label}>
            <Button
              unstyled
              width="100%"
              height="100%"
              cursor="pointer"
              onClick={event => {
                event.stopPropagation()
                openPatientTherapySequenceDrawer(row.original, {defaultTab: 'observations'})
              }}
            >
              <Text srOnly>{label}</Text>
              <Status.Root colorPalette="orange">
                <Status.Indicator />
              </Status.Root>
            </Button>
          </Tooltip>
        )
      },
      ...def,
    }),

  time: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'time',
      header: () => {
        const {t} = useTranslate('common')
        return <chakra.div width="100%">{t('time')}</chakra.div>
      },
      cell: ({row}) => {
        return (
          <chakra.div>
            <Text fontSize="sm" color="fg">
              {row.original.time}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              {getShortDateFormatter().format(row.original.therapyDate)}
            </Text>
          </chakra.div>
        )
      },
      ...def,
    }),

  patient: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'patient',
      header: () => {
        const {t} = useTranslate('common')
        return <chakra.div width="100%">{t('patient')}</chakra.div>
      },
      cell: ({row}) => <PatientCell sequence={row.original} />,
      ...def,
    }),

  patientWithHospitalId: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'patient',
      header: () => {
        const {t} = useTranslate('common')
        return <chakra.div width="100%">{t('patient')}</chakra.div>
      },
      cell: ({row}) => <PatientCell sequence={row.original} showHospitalId />,
      ...def,
    }),

  protocol: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'protocol',
      header: () => {
        const {t} = useTranslate('common')
        return <chakra.div width="100%">{t('protocol')}</chakra.div>
      },
      cell: ({row}) => {
        const {t} = useTranslate('common')

        if (row.original.isNote) {
          return (
            <Box display="flex" gapX="2" wordBreak="">
              <LuQuote color="black" />
              <Text fontWeight="medium" fontStyle="italic" fontSize="sm" color="fg.muted">
                {row.original.note}
              </Text>
            </Box>
          )
        }

        const protocol = row.original.protocol
        if (!protocol) return null

        return (
          <Box>
            <Text fontSize="sm">{protocol.name}</Text>
            <Text fontSize="xs" color="fg.muted" fontWeight="medium">
              {t('cycle_x_day_y', {cycle: row.original.cycle, day: row.original.day})}
            </Text>
          </Box>
        )
      },
      meta: {
        getCellContainerProps: sequence => {
          const props: BoxProps = {}
          if (sequence.isNote) {
            props.maxWidth = '55%'
          }
          return props
        },
      },
      ...def,
    }),

  treatment: (def?: AccessorColumnDef) =>
    sequenceColumnsHelper.accessor('medicineNames', {
      header: context => {
        const {t} = useTranslate('common')
        return (
          <SortableTableHeader {...context} width="100%">
            {t('treatment')}
          </SortableTableHeader>
        )
      },
      cell: ({row}) => {
        const {t} = useTranslate('common')

        if (row.original.isNote) return null

        const {names, dosages, carrierDetails} = formatTreatmentLabels(row.original, t)

        return (
          <Box>
            <Text fontSize="sm">{names.join(' / ')}</Text>
            <Text fontSize="xs" color="fg.muted" fontWeight="medium">
              {dosages.join(' / ')} {carrierDetails.join(' ')}
            </Text>
          </Box>
        )
      },
      enableSorting: false,
      meta: {
        getCellContainerProps: getHideableCellProps,
      },
      ...def,
    }),
  internalId: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'internalId',
      header: () => {
        const {t} = useTranslate('common')
        return <chakra.div width="100%">{t('prescription')}</chakra.div>
      },
      cell: ({row}) => {
        if (row.original.isNote) return null

        return (
          <chakra.div>
            <Text fontSize="sm" color="fg">
              {row.original.internalId}
            </Text>
          </chakra.div>
        )
      },
      meta: {
        getCellContainerProps: getHideableCellProps,
      },
      ...def,
    }),
  administration: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'administration',
      header: () => {
        const {t} = useTranslate('common')
        return <chakra.div width="100%">{t('administration')}</chakra.div>
      },
      cell: ({row}) => {
        const {t} = useTranslate('common')

        if (row.original.isNote) return null

        return (
          <Box>
            <Text fontSize="sm">{row.original.applicationForm?.name}</Text>
            <Text fontSize="xs" color="fg.muted" fontWeight="medium">
              {t('x_min', {value: row.original.duration || 0})}
            </Text>
          </Box>
        )
      },
      meta: {
        getCellContainerProps: getHideableCellProps,
      },
      ...def,
    }),

  journey: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'journey',
      header: () => {
        const {t} = useTranslate('common')
        return <chakra.div width="100%">{t('journey')}</chakra.div>
      },
      cell: ({row}) => {
        if (row.original.isNote) return null

        return <TherapySequenceJourneyTimeline sequence={row.original} />
      },
      meta: {
        getCellContainerProps: getHideableCellProps,
      },
      ...def,
    }),

  actions: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.display({
      id: 'actions',
      header: () => {
        const {t} = useTranslate('common')
        return <chakra.div width="100%">{t('actions')}</chakra.div>
      },
      cell: ({row}) => {
        return <TherapySequenceActions key={row.original.id} sequence={row.original} />
      },
      ...def,
    }),

  status: (def?: DisplayColumnDef) =>
    sequenceColumnsHelper.accessor('status', {
      header: () => {
        const {t} = useTranslate('common')
        return (
          <chakra.div display="flex" justifyContent="flex-end" width="100%">
            {t('status')}
          </chakra.div>
        )
      },
      cell: ({row}) => (
        <chakra.div display="flex" justifyContent="flex-end" width="100%">
          <ResourceStatus status={row.original.status} />
        </chakra.div>
      ),
      ...def,
    }),
}
